import apiClient from '../config/api';
import axios, { AxiosProgressEvent } from 'axios';

/**
 * Format de composition pour les images avant/après
 */
export type CompositeFormat = 'side-by-side' | 'vertical' | 'arrow';

/**
 * Métadonnées pour les images composites avant/après
 */
export interface CompositeMetadata {
  photoAvantId: number;      // ID de la photo AVANT originale
  photoApresId: number;       // ID de la photo APRÈS originale
  format: CompositeFormat;    // Format de composition
  zone?: string;              // Zone du chantier (ex: "Cuisine")
  description?: string;       // Description optionnelle
  includeContact?: boolean;   // Inclure les coordonnées de l'artisan
  dateGeneration: string;     // Date de génération de la composite
}

/**
 * Interface pour les informations de fichier
 */
export interface FileInfo {
  id: number;
  nom: string;
  category: 'PHOTO_CHANTIER';
  urlS3?: string;
  cheminLocal?: string;
  tailleFichier: number;
  mimeType: string;
  dateUpload: string;
  devisId?: number;
  factureId?: number;
  projetId?: number;

  // Métadonnées pour les images composites avant/après
  isComposite?: boolean;
  compositeMetadata?: CompositeMetadata;
}

/**
 * Interface pour l'URL présignée
 */
export interface PresignedUrlResponse {
  url: string;
  expiresIn: number; // en secondes
}

/**
 * Options pour l'upload de fichiers
 */
export interface UploadOptions {
  onProgress?: (progressEvent: AxiosProgressEvent) => void;
  signal?: AbortSignal;
}

/**
 * Service de gestion des fichiers
 * Gère l'upload, la récupération et la suppression de fichiers (PDF, photos)
 */
class FileService {
  /**
   * Upload d'un fichier vers le backend
   * @param file - Le fichier à uploader
   * @param category - La catégorie du fichier (PHOTO_CHANTIER)
   * @param entityId - ID de l'entité associée (devis, facture ou projet)
   * @param options - Options d'upload (progression, annulation)
   * @returns Les informations du fichier uploadé
   */
  async uploadFile(
    file: File,
    category: 'PHOTO_CHANTIER',
    entityId: number,
    options?: UploadOptions
  ): Promise<FileInfo> {
    console.log('📤 Upload de fichier:', {
      name: file.name,
      size: file.size,
      type: file.type,
      category,
      entityId,
    });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);

    // Déterminer l'endpoint selon la catégorie
    let endpoint = '';
    switch (category) {
      case 'PHOTO_CHANTIER':
        endpoint = `/api/projets/${entityId}/fichiers`;
        break;
      default:
        throw new Error(`Catégorie de fichier non supportée: ${category}`);
    }

    try {
      const response = await apiClient.post<FileInfo>(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: options?.onProgress,
        signal: options?.signal,
      });

      console.log('✅ Fichier uploadé avec succès:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de l\'upload du fichier:', error);
      if (axios.isCancel(error)) {
        throw new Error('Upload annulé');
      }
      throw error;
    }
  }

  /**
   * Upload de plusieurs fichiers en parallèle
   * @param files - Les fichiers à uploader
   * @param category - La catégorie des fichiers
   * @param entityId - ID de l'entité associée
   * @param options - Options d'upload
   * @returns Les informations des fichiers uploadés
   */
  async uploadMultipleFiles(
    files: File[],
    category: 'PHOTO_CHANTIER',
    entityId: number,
    options?: UploadOptions
  ): Promise<FileInfo[]> {
    console.log(`📤 Upload de ${files.length} fichiers en parallèle`);

    const uploadPromises = files.map((file) =>
      this.uploadFile(file, category, entityId, options)
    );

    try {
      const results = await Promise.all(uploadPromises);
      console.log(`✅ ${results.length} fichiers uploadés avec succès`);
      return results;
    } catch (error) {
      console.error('❌ Erreur lors de l\'upload multiple:', error);
      throw error;
    }
  }

  /**
   * Récupérer tous les fichiers d'une entité
   * @param category - La catégorie du fichier
   * @param entityId - ID de l'entité
   * @returns Liste des fichiers
   */
  async getFilesByEntity(
    category: 'PHOTO_CHANTIER',
    entityId: number
  ): Promise<FileInfo[]> {
    let endpoint = '';
    switch (category) {
      case 'PHOTO_CHANTIER':
        endpoint = `/api/projets/${entityId}/fichiers`;
        break;
      default:
        throw new Error(`Catégorie de fichier non supportée: ${category}`);
    }

    try {
      const response = await apiClient.get<FileInfo[]>(endpoint);
      console.log(`✅ ${response.data.length} fichier(s) récupéré(s)`);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de la récupération des fichiers:', error);
      throw error;
    }
  }

  /**
   * Récupérer un fichier par son ID
   * @param fileId - ID du fichier
   * @returns Les informations du fichier
   */
  async getFileById(fileId: number): Promise<FileInfo> {
    try {
      const response = await apiClient.get<FileInfo>(`/api/fichiers/${fileId}`);
      console.log('✅ Informations du fichier récupérées:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de la récupération du fichier:', error);
      throw error;
    }
  }

  /**
   * Obtenir une URL présignée pour accéder à un fichier sur S3
   * @param fileId - ID du fichier
   * @returns L'URL présignée valide pendant un temps limité
   */
  async getPresignedUrl(fileId: number): Promise<PresignedUrlResponse> {
    try {
      const response = await apiClient.get<PresignedUrlResponse>(
        `/api/fichiers/${fileId}/presigned-url`
      );
      console.log('✅ URL présignée générée, expire dans:', response.data.expiresIn, 'secondes');
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de la génération de l\'URL présignée:', error);
      throw error;
    }
  }

  /**
   * Télécharger un fichier
   * @param fileId - ID du fichier
   * @param fileName - Nom du fichier pour le téléchargement
   */
  async downloadFile(fileId: number, fileName?: string): Promise<void> {
    try {
      // Récupérer l'URL présignée
      const { url } = await this.getPresignedUrl(fileId);

      // Créer un lien de téléchargement
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'fichier';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log('✅ Téléchargement lancé:', fileName);
    } catch (error: any) {
      console.error('❌ Erreur lors du téléchargement:', error);
      throw error;
    }
  }

  /**
   * Supprimer un fichier
   * @param fileId - ID du fichier à supprimer
   */
  async deleteFile(fileId: number): Promise<void> {
    try {
      await apiClient.delete(`/api/fichiers/${fileId}`);
      console.log('✅ Fichier supprimé avec succès');
    } catch (error: any) {
      console.error('❌ Erreur lors de la suppression du fichier:', error);
      throw error;
    }
  }

  /**
   * Vérifier si un fichier existe
   * @param fileId - ID du fichier
   * @returns true si le fichier existe
   */
  async fileExists(fileId: number): Promise<boolean> {
    try {
      await this.getFileById(fileId);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Valider un fichier avant upload
   * @param file - Le fichier à valider
   * @param category - La catégorie du fichier
   * @returns true si valide, sinon throw une erreur
   */
  validateFile(
    file: File,
    category: 'PHOTO_CHANTIER'
  ): boolean {
    // Taille maximale: 10MB
    const MAX_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      throw new Error('La taille du fichier ne doit pas dépasser 10 MB');
    }

    // Validation du type MIME selon la catégorie
    const allowedTypes: { [key: string]: string[] } = {
      PHOTO_CHANTIER: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
    };

    const allowed = allowedTypes[category];
    if (!allowed.includes(file.type)) {
      throw new Error(
        `Type de fichier non autorisé. Types acceptés: ${allowed.join(', ')}`
      );
    }

    return true;
  }

  /**
   * Formater la taille d'un fichier en format lisible
   * @param bytes - Taille en bytes
   * @returns Taille formatée (ex: "1.5 MB")
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Générer une image composite avant/après
   * @param photoAvantId - ID de la photo AVANT
   * @param photoApresId - ID de la photo APRÈS
   * @param config - Configuration de la composition
   * @param entityId - ID du projet/devis associé
   * @returns Les informations du fichier composite généré
   */
  async generateBeforeAfterComposite(
    photoAvantId: number,
    photoApresId: number,
    config: {
      format: CompositeFormat;
      zone?: string;
      description?: string;
      includeContact?: boolean;
    },
    entityId: number
  ): Promise<FileInfo> {
    console.log('📸 Génération image composite avant/après:', {
      photoAvantId,
      photoApresId,
      format: config.format,
      zone: config.zone,
      entityId,
    });

    try {
      const response = await apiClient.post<FileInfo>(
        `/api/projets/${entityId}/fichiers/composite`,
        {
          photoAvantId,
          photoApresId,
          format: config.format,
          zone: config.zone,
          description: config.description,
          includeContact: config.includeContact,
        }
      );

      console.log('✅ Image composite générée avec succès:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de la génération de l\'image composite:', error);
      throw error;
    }
  }

  /**
   * Obtenir l'icône appropriée selon le type de fichier
   * @param mimeType - Type MIME du fichier
   * @returns Nom de l'icône
   */
  getFileIcon(mimeType: string): string {
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType === 'application/pdf') return '📄';
    return '📎';
  }
}

export default new FileService();
